import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { fetchElevationData, generateLocationSpecificData, getColorForElevation } from './terrainUtils';
import './TerrainPopUp.css';
import cursorManager from '../../../utils/cursorManager.js';

// popupPosition added to props
export default function TerrainPopup({ lat, lon }) {
  const mountRef = useRef(null); // Ref for the Three.js canvas container
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const terrainMeshRef = useRef(null); // Ref for the 3D terrain object
  const animationRef = useRef(null); // Ref for the animation ID
  const controlsRef = useRef(null); // Ref to store custom controls

  const [isLoading, setIsLoading] = useState(true); // State for loading indicator
  const [loadError, setLoadError] = useState(null); // State for loading errors
  const [elevationRange, setElevationRange] = useState({ min: 0, max: 0 }); // Elevation range

  // Definition of the color list for the legend
  const colorLegend = [
    { value: 0.0, color: '#0a0a55', label: 'Deep Ocean (0%)' },
    { value: 0.05, color: '#1e1e80', label: 'Deep Water (5%)' },
    { value: 0.15, color: '#2a52be', label: 'Navy Blue (15%)' },
    { value: 0.25, color: '#4682b4', label: 'Steel Blue (25%)' },
    { value: 0.35, color: '#87ceeb', label: 'Shallow Water (35%)' },
    { value: 0.45, color: '#98fb98', label: 'Wetland (45%)' },
    { value: 0.50, color: '#32cd32', label: 'Low Land (50%)' },
    { value: 0.60, color: '#6b8e23', label: 'Mid Land (60%)' },
    { value: 0.70, color: "#a5a134", label: 'Mid Elevation (70%)' },
    { value: 0.80, color: "#f0df28", label: 'High Elevation (80%)' },
    { value: 0.90, color: "#fb6a09", label: 'Mountains (90%)' },
    { value: 1.0, color: "#db1919", label: 'High Peaks (100%)' }
  ];

  // --- Custom controls for the 3D camera ---
  const setupControls = useCallback((camera, renderer) => {
    let isDragging = false;
    let isRightClick = false; // To differentiate drag for rotation and panning
    let previousMousePosition = { x: 0, y: 0 };
    let cameraDistance = 400; // Initial camera distance
    let cameraAngleX = 0; // Horizontal rotation angle
    let cameraAngleY = 0.4; // Vertical rotation angle

    const canvas = renderer.domElement; // The Three.js canvas element

    // Function to update camera position based on angles and distance
    const updateCameraPosition = () => {
      camera.position.x = Math.sin(cameraAngleX) * Math.cos(cameraAngleY) * cameraDistance;
      camera.position.y = Math.sin(cameraAngleY) * cameraDistance;
      camera.position.z = Math.cos(cameraAngleX) * Math.cos(cameraAngleY) * cameraDistance;
      camera.lookAt(0, 0, 0); // Camera always looks at the scene center
    };

    // Mouse event handlers
    const handleMouseDown = (e) => {
      isDragging = true;
      isRightClick = e.button === 2; // Right mouse button
      previousMousePosition = { x: e.clientX, y: e.clientY };
      cursorManager.setCursor('terrain-3d-drag', 'grabbing', { current: { getCanvas: () => canvas } });
      e.preventDefault(); // Prevent right-click context menu
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const deltaMove = {
        x: e.clientX - previousMousePosition.x,
        y: e.clientY - previousMousePosition.y
      };

      if (isRightClick) {
        // Panning (lateral movement) with right button
        const moveSpeed = 0.8;
        const right = new THREE.Vector3();
        const up = new THREE.Vector3();
        camera.getWorldDirection(new THREE.Vector3()); // Get camera direction
        right.setFromMatrixColumn(camera.matrix, 0); // 'Right' vector of the camera
        up.setFromMatrixColumn(camera.matrix, 1);    // 'Up' vector of the camera

        camera.position.add(right.clone().multiplyScalar(-deltaMove.x * moveSpeed));
        camera.position.add(up.clone().multiplyScalar(deltaMove.y * moveSpeed));
        camera.lookAt(0, 0, 0); // Look back at the center after panning
      } else {
        // Rotation with left button
        cameraAngleX += deltaMove.x * 0.01;
        cameraAngleY += deltaMove.y * 0.01;
        // Limit vertical angle to prevent camera flip
        cameraAngleY = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, cameraAngleY));
        updateCameraPosition();
      }

      previousMousePosition = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging = false;
      isRightClick = false;
      cursorManager.resetCursor('terrain-3d-drag', { current: { getCanvas: () => canvas } });
    };

    const handleWheel = (e) => {
      // Zoom in/out with mouse wheel
      cameraDistance += e.deltaY * 0.5;
      // Limit camera distance
      cameraDistance = Math.max(100, Math.min(1000, cameraDistance));
      updateCameraPosition();
    };

    const handleContextMenu = (e) => {
      e.preventDefault(); // Prevent native browser context menu
    };

    // Add event listeners
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel);
    canvas.addEventListener('contextmenu', handleContextMenu);

    updateCameraPosition(); // Set initial camera position

    // Return a cleanup function to remove listeners
    return {
      cleanup: () => {
        canvas.removeEventListener('mousedown', handleMouseDown);
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseup', handleMouseUp);
        canvas.removeEventListener('wheel', handleWheel);
        canvas.removeEventListener('contextmenu', handleContextMenu);
        // Reset cursor state
        cursorManager.resetCursor('terrain-3d-drag', { current: { getCanvas: () => canvas } });
      }
    };
  }, []); // Dependencies: useCallback memoizes if dependencies don't change

  // --- Load and create the 3D terrain model ---
  const loadTerrain = useCallback(async () => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    const camera = cameraRef.current;

    if (!scene || !renderer || !camera) return; // Ensure Three.js is initialized

    setIsLoading(true); // Show loading indicator
    setLoadError(null); // Clear previous errors

    // Clear previous terrain if it exists
    if (terrainMeshRef.current) {
      scene.remove(terrainMeshRef.current);
      terrainMeshRef.current.geometry.dispose();
      terrainMeshRef.current.material.dispose();
      terrainMeshRef.current = null;
    }

    let elevationData;
    let minElevation;
    let maxElevation;
    let tileSize;

    try {
      console.log(`Loading real AWS terrain for ${lat}, ${lon}...`);
      // Try to get real elevation data from AWS
      elevationData = await fetchElevationData(lat, lon, 10); // Zoom level 10
      tileSize = Math.sqrt(elevationData.length); // Tile size (e.g., 256x256)
      minElevation = Math.min(...elevationData);
      maxElevation = Math.max(...elevationData);

      // Check if data forms a perfect square
      if (tileSize * tileSize !== elevationData.length) {
        throw new Error('Elevation data does not form a perfect square. Using simulated data.');
      }
      console.log(`Real elevation range: ${minElevation.toFixed(1)}m - ${maxElevation.toFixed(1)}m`);

    } catch (error) {
      console.error('Error loading real data:', error);
      setLoadError(`Error: ${error.message || 'Could not load real terrain.'} Using simulated data.`);
      console.warn('Using simulated data as fallback...');

      // If real data loading fails, use simulated data
      tileSize = 256; // Simulated tile size
      elevationData = generateLocationSpecificData(lat, lon, tileSize, tileSize);
      minElevation = Math.min(...elevationData);
      maxElevation = Math.max(...elevationData);
      console.log(`Simulated elevation range: ${minElevation.toFixed(1)}m - ${maxElevation.toFixed(1)}m`);
    }

    setElevationRange({ min: minElevation, max: maxElevation }); // Update elevation range

    const terrainSize = 500; // Terrain plane size in Three.js units
    // Create a plane geometry with segments for each elevation point
    const geometry = new THREE.PlaneGeometry(terrainSize, terrainSize, tileSize - 1, tileSize - 1);
    const vertices = geometry.attributes.position.array; // Access geometry vertices
    const colors = []; // Array to store vertex colors

    const elevationSpread = maxElevation - minElevation;
    let heightScale = 0.25; // Default height scale

    // Dynamically adjust height scale based on elevation range
    if (elevationSpread > 2000) {
      heightScale = 0.1; // For large ranges (very high mountains)
    } else if (elevationSpread < 100) {
      heightScale = 2.5; // For small ranges (flat terrain)
    } else if (elevationSpread < 500) {
      heightScale = 0.5; // For medium ranges
    }

    // Apply elevation and color to each vertex
    for (let i = 0; i < elevationData.length; i++) {
      const elevation = elevationData[i];
      // Adjust Z coordinate (height) of the vertex
      vertices[i * 3 + 2] = (elevation - minElevation) * heightScale;

      // Get color based on normalized elevation
      const color = getColorForElevation(elevation, minElevation, maxElevation);
      colors.push(color.r, color.g, color.b); // Add RGB components to the colors array
    }

    geometry.attributes.position.needsUpdate = true; // Mark vertices as updated
    // Add color attribute to geometry
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals(); // Recalculate normals for correct lighting

    // Create terrain material with vertex colors
    const material = new THREE.MeshLambertMaterial({
      vertexColors: true, // Enable vertex colors
      side: THREE.DoubleSide // Render both sides of the mesh
    });

    // Create terrain mesh
    terrainMeshRef.current = new THREE.Mesh(geometry, material);
    terrainMeshRef.current.rotation.x = -Math.PI / 2; // Rotate plane to be horizontal
    terrainMeshRef.current.receiveShadow = true; // Can receive shadows
    terrainMeshRef.current.castShadow = true;    // Can cast shadows

    scene.add(terrainMeshRef.current); // Add terrain to the scene

    setIsLoading(false); // Hide loading indicator
    console.log(`✅ 3D Terrain rendered for ${lat}, ${lon}`);

  }, [lat, lon]); // Dependencies: re-runs if lat or lon change

  // --- useEffect hook for Three.js initialization (runs only once on mount) ---
  useEffect(() => {
    const currentMount = mountRef.current;
    if (!currentMount) return;

    // SCENE
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff); // White background 
    scene.fog = new THREE.Fog(0xffffff, 300, 1000); // White fog
    sceneRef.current = scene;

    // CAMERA
    const camera = new THREE.PerspectiveCamera(
      75, // Field of view
      currentMount.clientWidth / currentMount.clientHeight, // Aspect ratio
      0.1, // Near plane
      2000 // Far plane
    );
    camera.position.set(0, 250, 400); // Initial camera position
    cameraRef.current = camera;

    // RENDERER
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); // antialias for smooth edges, alpha for transparent background
    renderer.setSize(currentMount.clientWidth, currentMount.clientHeight); // Set renderer size
    renderer.shadowMap.enabled = true; // Enable shadow maps
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Shadow map type
    currentMount.appendChild(renderer.domElement); // Add canvas to DOM
    rendererRef.current = renderer;

    // CONTROLS
    controlsRef.current = setupControls(camera, renderer); // Initialize custom controls

    // LIGHTING
    const ambientLight = new THREE.AmbientLight(0x404040, 0.4); // Soft ambient light
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1); // Main directional light
    directionalLight.position.set(300, 400, 200); // Light position
    directionalLight.castShadow = true; // Light casts shadows
    // Shadow configuration
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 1000;
    directionalLight.shadow.camera.left = -400;
    directionalLight.shadow.camera.right = 400;
    directionalLight.shadow.camera.top = 400;
    directionalLight.shadow.camera.bottom = -400;
    scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0x4080ff, 0.3); // Fill light to soften shadows
    fillLight.position.set(-200, 300, -200);
    scene.add(fillLight);

    // RENDERING LOOP (animation)
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate); // Request next frame
      renderer.render(scene, camera); // Render the scene
    };
    animate(); // Start animation loop

    // Handle popup container resizing
    const handleResize = () => {
      if (currentMount && cameraRef.current && rendererRef.current) {
        const width = currentMount.clientWidth;
        const height = currentMount.clientHeight;

        cameraRef.current.aspect = width / height; // Update camera aspect ratio
        cameraRef.current.updateProjectionMatrix(); // Update projection matrix
        rendererRef.current.setSize(width, height); // Adjust renderer size
      }
    };

    // Use ResizeObserver to detect size changes in the mountRef element
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.target === currentMount) {
          handleResize(); // Call resize handler
        }
      }
    });

    resizeObserver.observe(currentMount); // Observe the canvas container

    // Cleanup function when component unmounts
    return () => {
      if (controlsRef.current) {
        controlsRef.current.cleanup(); // Clean up control listeners
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current); // Cancel animation loop
      }
      if (terrainMeshRef.current) {
        sceneRef.current.remove(terrainMeshRef.current);
        terrainMeshRef.current.geometry.dispose(); // Free geometry memory
        terrainMeshRef.current.material.dispose(); // Free material memory
        terrainMeshRef.current = null;
      }
      if (renderer.domElement && currentMount) {
        currentMount.removeChild(renderer.domElement); // Remove canvas from DOM
      }
      renderer.dispose(); // Dispose renderer resources
      scene.clear(); // Clear the scene
      resizeObserver.disconnect(); // Disconnect ResizeObserver
    };

  }, [setupControls]); // Dependency on setupControls for proper initialization

  // useEffect hook to load terrain when location (lat/lon) changes
  useEffect(() => {
    if (lat !== null && lon !== null) {
      loadTerrain(); // Call function to load terrain
    }
  }, [lat, lon, loadTerrain]); // Dependency on loadTerrain is important for useCallback

  // // Calculate popup position style
  // const popupStyle = popupPosition
  //   ? {
  //       position: 'absolute',
  //       left: `${popupPosition.x}px`,
  //       top: `${popupPosition.y}px`,
  //       // Center the popup over the click point, adjusting for its size
  //       transform: 'translate(-50%, -100%)', 
  //       zIndex: 1000
  //     }
  //   : {};

  return (
    <div className="terrain-popup">
      <div className="terrain-popup__inner">

        <div className="terrain-popup__header">
          <div>
            <h2 className="terrain-popup__title">{lat.toFixed(4)}°, {lon.toFixed(4)}°</h2>
          </div>
        </div>

        <div className="terrain-popup__canvas-wrapper">
          <div ref={mountRef} className="terrain-popup__canvas"></div>

          {isLoading && (
            <div className="terrain-popup__loading">
              <div className="terrain-popup__spinner"></div>
              <div className="terrain-popup__loading-text">Loading 3D model...</div>
            </div>
          )}

          {loadError && (
            <div className="terrain-popup__error">
              ⚠️ Using simulated data
            </div>
          )}
        </div>

        <div className="terrain-popup__stats">
          <div className="terrain-popup__stats-header">
            <h3>Elevation</h3>
            <span>AWS Terrain • Z10</span>
          </div>
          <div className="terrain-popup__stats-values">
            <div><label>Minimum:</label><span>{elevationRange.min.toFixed(1)}m</span></div>
            <div><label>Maximum:</label><span>{elevationRange.max.toFixed(1)}m</span></div>
            <div><label>Range:</label><span>{(elevationRange.max - elevationRange.min).toFixed(1)}m</span></div>
          </div>
        </div>

        <div className="terrain-popup__legend">
          <div className="terrain-popup__legend-header">
            <h3>Color Legend</h3>
            <span>Relative Elevation</span>
          </div>
          <div className="terrain-popup__legend-grid">
            {colorLegend.map((item, i) => (
              <div key={i} className="terrain-popup__legend-item">
                <div className="terrain-popup__legend-swatch" style={{ backgroundColor: item.color }}></div>
                <span className="terrain-popup__legend-label">{item.label}</span>
                <span className="terrain-popup__legend-value">{(item.value * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
